"use client";

import { AnimateNumber } from "motion-plus/react";
import { motion } from "motion/react";
import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

export function NumberCounter({
  value,
  onChange,
  min = 1,
  max = 99,
  className,
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  className?: string;
}) {
  const decrement = () => onChange(Math.max(min, value - 1));
  const increment = () => onChange(Math.min(max, value + 1));

  return (
    <div
      className={cn(
        "flex h-11 w-[132px] box-border items-center gap-2 rounded-[14px] bg-black px-2 py-1.5 shadow-[0_4px_12px_rgba(0,0,0,0.25)]",
        className,
      )}
    >
      <motion.button
        type="button"
        aria-label="Decrease quantity"
        onClick={decrement}
        disabled={value <= min}
        className="flex size-7 shrink-0 items-center justify-center rounded-full border-none bg-white/20 text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
        whileHover={value > min ? { scale: 1.05 } : {}}
        whileTap={value > min ? { scale: 0.95 } : {}}
      >
        <Minus className="size-3.5" strokeWidth={2.5} />
      </motion.button>
      <div className="flex min-w-0 flex-1 items-center justify-center">
        <AnimateNumber
          transition={{
            visualDuration: 0.35,
            type: "spring",
            bounce: 0.2,
            opacity: { duration: 0.15, ease: "linear" },
          }}
          className="text-sm font-extrabold text-white"
        >
          {value}
        </AnimateNumber>
      </div>
      <motion.button
        type="button"
        aria-label="Increase quantity"
        onClick={increment}
        disabled={value >= max}
        className="flex size-7 shrink-0 items-center justify-center rounded-full border-none bg-white/20 text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
        whileHover={value < max ? { scale: 1.05 } : {}}
        whileTap={value < max ? { scale: 0.95 } : {}}
      >
        <Plus className="size-3.5" strokeWidth={2.5} />
      </motion.button>
    </div>
  );
}
